import React, { useMemo } from 'react';

import FinalFormInput from '@zola/zola-ui/src/components/Form/inputV3/FinalFormInput';
import { InputFieldV3Props } from '@zola/zola-ui/src/components/Form/inputV3/InputFieldV3/InputFieldV3';
import {
  InputFieldContainer,
  InputSection,
} from '@zola/zola-ui/src/components/Form/inputV3/InputFieldV3/InputFieldV3.styles';
import { composeValidators } from '@zola/zola-ui/src/components/Form/util';

import styled from '@emotion/styled';
import cx from 'classnames';
import { FieldValidator } from 'final-form';

import {
  NUMBER as NUMBER_VALIDATOR,
  GREATER_THAN_ZERO_OR_BLANK,
  REQUIRED_TEXT as REQUIRED,
  maxIntegerValue,
} from '~/components/common/form/commonValidations';
import { FormWithMutators } from '~/components/common/form/formWithMutators';
import { NumberInputWithCommas } from '~/components/common/zolaUI/NumberInputWithCommas/NumberInputWithCommas';

import styles from './numberInputField.module.less';

/** Fixes a stupid top margin on the InputSection of an InputFieldV3 */
const ZolaUiMarginFixer = styled.div`
  ${InputFieldContainer} {
    ${InputSection} {
      margin-top: 0;
    }
  }
`;

export type NumberInputFieldProps<FormValues extends Record<string, any>> = {
  name: keyof FormValues;
  label?: string;
  placeholder?: string;
  type?: 'number' | 'currency';
  /** Maximum allowed value for the input.  Defaults to 1 million */
  maxValue?: number;
  required?: boolean;
  inputOnChange?: () => void;
  /** Custom validator for the field, ie. that returns a custom error message */
  requiredCustomValidator?: (value: any) => string | undefined;
  className?: string;
} & Omit<InputFieldV3Props, 'validate'>;

const IntegerInputField = <FormValues extends Record<string, any>>({
  validators,
  required: _,
  label,
  placeholder,
  name,
  inputOnChange,
  className,
  ...rest
}: Omit<NumberInputFieldProps<FormValues>, 'type' | 'maxValue' | 'requiredCustomValidator'> & {
  validators: FieldValidator<string>[];
}): JSX.Element => {
  const fieldProps = {
    min: 0,
    type: 'number',
  };

  const validate = useMemo(() => {
    return composeValidators(...validators, NUMBER_VALIDATOR);
  }, [validators]);

  return (
    <FinalFormInput
      label={label}
      placeholder={placeholder}
      validate={validate}
      name={name}
      id={name}
      allowChangeToEmpty
      inputMode="numeric"
      containerClassname={cx(styles.numberInputField, className)}
      onChange={inputOnChange}
      {...fieldProps}
      {...rest}
    />
  );
};

/**
 * A number input field, in a react final form, that can be either a currency
 * input or a strict number input.
 *
 * For currency, the field accepts commas as inputs and will format the value
 * with commas.  Currency inputs use a second field ${name}AsCurrency to store
 * the _formatted_ value.  The form value, ${name} will be a _string_.
 *
 * @param param0
 * @returns
 */
export const NumberInputField = <FormValues extends Record<string, any>>({
  type,
  maxValue,
  requiredCustomValidator,
  ...rest
}: NumberInputFieldProps<FormValues>) => {
  const { required, label, inputOnChange, className, name } = rest;

  /** Construct a single validator to pass to the RFF Field component */
  const validators: FieldValidator<string>[] = useMemo(() => {
    const allValidators: FieldValidator<string>[] = [];

    if (required && requiredCustomValidator) {
      allValidators.push(requiredCustomValidator);
    } else if (required) {
      allValidators.push(REQUIRED);
    }

    allValidators.push(GREATER_THAN_ZERO_OR_BLANK);

    allValidators.push(maxIntegerValue(maxValue || 1000000));

    return allValidators;
  }, [maxValue, required, requiredCustomValidator]);

  const input =
    type === 'currency' ? (
      <NumberInputWithCommas<FormValues, FormWithMutators<FormValues>>
        validators={validators}
        displayedField={`${name}AsCurrency` as keyof FormValues}
        onChange={inputOnChange}
        containerClassname={cx(styles.numberInputField, className)}
        id={name}
        type="currency"
        {...rest}
      />
    ) : (
      <IntegerInputField validators={validators} {...rest} />
    );

  if (label) {
    return input;
  }
  return <ZolaUiMarginFixer>{input}</ZolaUiMarginFixer>;
};
